import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './CompInvitations.css';
import { Alert, Button, EmptyState, PageHeader, Skeleton, StatusBadge, Surface } from '../components/ui/Primitives';
import {
  createCompInvitation,
  getCompInvitations,
  revokeCompInvitation,
} from '../services/compInvitationApi';


const initialValues = {
  email: '',
  subscriptionType: 'business',
  expiresInDays: 30,
  notes: '',
};


const formatDate = (value) => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '—';


const statusTone = (status) => ({
  pending: 'info',
  redeemed: 'processing',
  provisioned: 'success',
  revoked: 'danger',
  expired: 'neutral',
}[status] || 'neutral');


const errorMessage = (error, fallback) => error?.response?.data?.error?.message || fallback;


function CompInvitations({ checkLogin }) {
  const navigate = useNavigate();
  const [values, setValues] = useState(initialValues);
  const [invitations, setInvitations] = useState([]);
  const [createdInvitation, setCreatedInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const loadInvitations = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setInvitations(await getCompInvitations());
    } catch (requestError) {
      if (requestError?.response?.status === 403) {
        navigate('/overview', { replace: true });
        return;
      }
      setError(errorMessage(requestError, 'Tester invitations could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const session = checkLogin?.();
    if (!session?.is_admin) {
      navigate('/overview', { replace: true });
      return;
    }
    loadInvitations();
  }, [checkLogin, loadInvitations, navigate]);

  const createInvitation = async (event) => {
    event.preventDefault();
    setCreating(true);
    setError('');
    setCreatedInvitation(null);
    setCopied(false);
    try {
      const invitation = await createCompInvitation(values);
      setCreatedInvitation(invitation);
      setValues(initialValues);
      await loadInvitations();
    } catch (requestError) {
      setError(errorMessage(requestError, 'The tester invitation could not be created.'));
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(createdInvitation.signupUrl);
      setCopied(true);
    } catch (_error) {
      setError('The link could not be copied automatically. Select and copy it below.');
    }
  };

  const revokeInvitation = async (invitation) => {
    const action = invitation.status === 'provisioned'
      ? 'This immediately cancels the Stripe subscription and ends the tester’s access. Continue?'
      : 'This permanently disables the invitation link. Continue?';
    if (!window.confirm(action)) return;
    setRevokingId(invitation.id);
    setError('');
    try {
      await revokeCompInvitation(invitation.id);
      await loadInvitations();
    } catch (requestError) {
      setError(errorMessage(requestError, 'The tester invitation could not be revoked.'));
    } finally {
      setRevokingId('');
    }
  };

  if (loading && !invitations.length) {
    return <div className="page-content comp-invitations-page"><Skeleton width="46%" height={42} /><Skeleton height={300} /><Skeleton height={300} /></div>;
  }

  return (
    <div className="page-content comp-invitations-page">
      <PageHeader
        eyebrow="Platform administration"
        title="Comped tester invitations"
        description="Issue private, single-use signup links backed by an active Stripe subscription with all charges fully comped."
        actions={<Button variant="secondary" to="/operations">Back to operations</Button>}
      />

      {error ? <Alert tone="danger" title="Tester invitations need attention">{error}</Alert> : null}
      <Alert tone="info" title="All tester usage is comped">
        Each account receives its selected plan plus unlimited metered overages at $0. Links can optionally be locked to one email address.
      </Alert>

      <Surface className="comp-invitation-builder">
        <div className="comp-invitation-heading">
          <div><span className="say-eyebrow">New invitation</span><h2>Create a private signup link</h2><p>The raw invitation token is shown once and only its hash is stored.</p></div>
        </div>
        <form onSubmit={createInvitation}>
          <label><span>Email restriction <small>Optional</small></span><input type="email" value={values.email} onChange={event => setValues(current => ({ ...current, email: event.target.value }))} placeholder="tester@example.com" /></label>
          <label><span>Plan</span><select value={values.subscriptionType} onChange={event => setValues(current => ({ ...current, subscriptionType: event.target.value }))}><option value="solo">Solo</option><option value="business">Business</option><option value="enterprise">Enterprise</option></select></label>
          <label><span>Expires after</span><div className="comp-expiry-input"><input type="number" min="1" max="365" value={values.expiresInDays} onChange={event => setValues(current => ({ ...current, expiresInDays: event.target.value }))} /><small>days</small></div></label>
          <label className="comp-notes-field"><span>Internal notes <small>Optional</small></span><textarea rows="3" maxLength="500" value={values.notes} onChange={event => setValues(current => ({ ...current, notes: event.target.value }))} placeholder="Who this tester is and what they are evaluating" /></label>
          <div className="comp-builder-actions"><span>Anyone with an unrestricted link can redeem it once.</span><Button type="submit" loading={creating} loadingLabel="Creating secure link…">Create invitation</Button></div>
        </form>
      </Surface>

      {createdInvitation ? (
        <Surface className="comp-created-link">
          <div><span className="say-eyebrow">Copy this now</span><h2>Invitation ready</h2><p>This exact link cannot be retrieved again after you leave this page.</p></div>
          <div className="comp-link-row"><input readOnly value={createdInvitation.signupUrl} onFocus={event => event.target.select()} /><Button variant="secondary" onClick={copyLink}>{copied ? 'Copied' : 'Copy link'}</Button></div>
        </Surface>
      ) : null}

      <Surface className="comp-invitation-history">
        <div className="comp-invitation-heading"><div><span className="say-eyebrow">Audit trail</span><h2>Recent tester invitations</h2><p>Provisioned accounts remain tied to their originating invitation.</p></div></div>
        {invitations.length ? (
          <div className="comp-invitation-table-wrap">
            <table><thead><tr><th>Recipient</th><th>Plan</th><th>Status</th><th>Expires</th><th>Notes</th><th /></tr></thead><tbody>{invitations.map(invitation => (
              <tr key={invitation.id}>
                <td><strong>{invitation.email || 'Any email'}</strong>{invitation.redeemedEmail && invitation.redeemedEmail !== invitation.email ? <small>Used by {invitation.redeemedEmail}</small> : null}</td>
                <td>{invitation.subscriptionType}</td>
                <td><StatusBadge status={statusTone(invitation.status)}>{invitation.status}</StatusBadge>{invitation.organizationId ? <small>{invitation.organizationId}</small> : null}</td>
                <td>{formatDate(invitation.expiresAt)}</td>
                <td>{invitation.notes || '—'}</td>
                <td>{['pending', 'redeemed', 'provisioned'].includes(invitation.status) ? <Button size="sm" variant="danger" loading={revokingId === invitation.id} onClick={() => revokeInvitation(invitation)}>{invitation.status === 'provisioned' ? 'End access' : 'Revoke'}</Button> : null}</td>
              </tr>
            ))}</tbody></table>
          </div>
        ) : <EmptyState title="No tester invitations yet" description="Create the first private comped signup link above." />}
      </Surface>
    </div>
  );
}


export default CompInvitations;
